import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from '@/lib/i18n';
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import JobManager from "@/components/JobManager";

import {
  Inquiry,
  InquiryStatus,
  deleteInquiry,
  getInquiries,
  updateInquiryStatus,
  updateInquiryStatusOnS3,
  loadAllInquiriesFromS3,
} from "@/lib/inquiries";
import { useToast } from "@/hooks/use-toast";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  S3Client,
  ListObjectsV2Command,
  GetObjectCommand,
  PutObjectCommand,
  DeleteObjectCommand,
  CopyObjectCommand,
} from "@aws-sdk/client-s3";

import {
  downloadS3File,
  previewS3File,
  uploadInquiryFiles,
} from "@/lib/awsS3Storage";

const fieldClass = "border rounded px-2 py-1 w-full";

/* AWS ENV */
const AWS_REGION = import.meta.env.VITE_AWS_REGION;
const AWS_ACCESS_KEY_ID = import.meta.env.VITE_AWS_ACCESS_KEY_ID;
const AWS_SECRET_ACCESS_KEY = import.meta.env.VITE_AWS_SECRET_ACCESS_KEY;
const S3_BUCKET = import.meta.env.VITE_AWS_S3_BUCKET;

/* Admin Login */
const SESSION_KEY = "viaMedicaAdminAuth";
const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || "najbolji0708";

/* S3 Client */
const s3 = new S3Client({
  region: AWS_REGION,
  credentials: {
    accessKeyId: AWS_ACCESS_KEY_ID,
    secretAccessKey: AWS_SECRET_ACCESS_KEY,
  },
});

/* Datei-Typ erkennen */
const detectTypeFromFilename = (name: string): "nurse" | "employer" | "contact" => {
  const lower = name.toLowerCase();
  const nurseKeywords = [
    "bewerbung",
    "cv",
    "lebenslauf",
    "diplom",
    "zeugnis",
    "pass",
    "id",
    "nurse",
    "candidate",
    "pfleger",
    "pflege",
    "medic",
    "health",
    "resume",
  ];
  const employerKeywords = [
    "firma",
    "company",
    "employer",
    "job",
    "request",
    "anfrage",
    "vacancy",
    "arbeitgeber",
    "recruit",
    "hr",
    "position",
  ];
  if (nurseKeywords.some((k) => lower.includes(k))) return "nurse";
  if (employerKeywords.some((k) => lower.includes(k))) return "employer";
  return "contact";
};

/* Datei-Liste aus S3 */
const useAdminFileList = () => {
  const [files, setFiles] = useState<
    { type: string; name: string; size: number; lastModified: number }[]
  >([]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await s3.send(
          new ListObjectsV2Command({
            Bucket: S3_BUCKET,
            Prefix: "inquiries/downloads/",
          })
        );
        const list =
          response.Contents?.filter((obj) => obj.Key && obj.Key !== "inquiries/downloads/")
            .map((obj) => {
              const parts = obj.Key!.split("/");
              return {
                type: parts[2],
                name: parts.slice(3).join("/"),
                size: obj.Size || 0,
                lastModified: new Date(obj.LastModified!).getTime(),
              };
            })
            .sort((a, b) => b.lastModified - a.lastModified) || [];
        setFiles(list);
      } catch {
        setFiles([]);
      }
    };

    load();
  }, []);

  return files;
};

/* Upload */
const FileUploadForm = () => {
    const { tr } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [action, setAction] = useState("download"); // default

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('Upload-Handler gestartet');

    const file = fileInputRef.current?.files?.[0];
    console.log('File:', file);
    console.log('File typeof:', typeof file);
    if (!file) {
      setMessage(tr('admin.selectFile'));
      return;
    }

    const type = detectTypeFromFilename(file.name);
    const key = `inquiries/downloads/${type}/${file.name}`;

    try {
      const arrayBuffer = await file.arrayBuffer();
      await s3.send(
        new PutObjectCommand({
          Bucket: S3_BUCKET,
          Key: key,
          Body: arrayBuffer,
          ContentType: file.type,
          Metadata: {
            action: action // speichert die Auswahl als Metadaten
          }
        })
      );

      const url = `https://${S3_BUCKET}.s3.${AWS_REGION}.amazonaws.com/${key}`;
      setDownloadUrl(url);
      setMessage(tr('admin.uploadSuccess'));
    } catch (err: any) {
      console.error('Upload-Fehler:', err);
      setMessage(err?.message || tr('admin.uploadError'));
    }
  };

  const files = useAdminFileList();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async () => {
    if (!selectedFile) return;
    setDeleteLoading(true);
    try {
      // Finde die Datei aus der Liste
      const fileObj = files.find(f => `${f.type}/${f.name}` === selectedFile);
      if (!fileObj) throw new Error(tr('admin.fileNotFound'));
      await s3.send(
        new DeleteObjectCommand({
          Bucket: S3_BUCKET,
          Key: `inquiries/downloads/${fileObj.type}/${fileObj.name}`,
        })
      );
      setMessage(tr('admin.deleteSuccess'));
      setSelectedFile(null);
      window.location.reload();
    } catch (err) {
      setMessage(tr('admin.deleteError'));
    }
    setDeleteLoading(false);
  };

  return (
    <>
      <form onSubmit={handleUpload} className="flex flex-col gap-2">
        <input type="file" ref={fileInputRef} className="border rounded px-2 py-1" />
        <div className="flex gap-4 mt-2">
          <label>
            <input
              type="radio"
              name="action"
              value="lesen"
              checked={action === "lesen"}
              onChange={() => setAction("lesen")}
            /> {tr('admin.read')}
          </label>
          <label>
            <input
              type="radio"
              name="action"
              value="download"
              checked={action === "download"}
              onChange={() => setAction("download")}
            /> {tr('admin.download')}
          </label>
          <label>
            <input
              type="radio"
              name="action"
              value="beides"
              checked={action === "beides"}
              onChange={() => setAction("beides")}
            /> {tr('admin.both')}
          </label>
        </div>
        <Button type="submit">{tr('admin.upload')}</Button>
        {message && <span className="text-xs text-muted-foreground">{message}</span>}
        {downloadUrl && (
          <a href={downloadUrl} target="_blank" className="text-xs text-primary underline mt-2">
            {tr('admin.downloadLink')}
          </a>
        )}
      </form>
      <div className="mt-6">
        <h3 className="font-medium mb-2">{tr('admin.uploadedFiles')}</h3>
        {files.length === 0 && <p className="text-sm text-muted-foreground">{tr('admin.noFiles')}</p>}
        {files.map((file) => (
          <div key={file.name} className="flex items-center gap-3 border rounded p-2 mb-2">
            <input
              type="radio"
              name="selectedFile"
              checked={selectedFile === `${file.type}/${file.name}`}
              onChange={() => setSelectedFile(`${file.type}/${file.name}`)}
            />
            <span className="flex-1">{file.name}</span>
            <span className="text-xs text-muted-foreground">{file.size} B</span>
            <Button size="sm" variant="destructive" disabled={deleteLoading || selectedFile !== `${file.type}/${file.name}`}
              onClick={handleDelete}>{tr('admin.delete')}</Button>
          </div>
        ))}
      </div>
    </>
  );
};

/* Datei-Manager */
const AdminFileManager = () => {
    const { tr } = useLanguage();
  const files = useAdminFileList();
  const [selected, setSelected] = useState<{ type: string; name: string }[]>([]);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleSelect = (file, checked) => {
    setSelected((prev) =>
      checked ? [...prev, file] : prev.filter((f) => f.name !== file.name)
    );
  };

  const handleDeleteFiles = async () => {
    setLoading(true);
    setError(null);

    try {
      for (const file of selected) {
        await s3.send(
          new DeleteObjectCommand({
            Bucket: S3_BUCKET,
            Key: `inquiries/downloads/${file.type}/${file.name}`,
          })
        );
      }
      window.location.reload();
    } catch {
      setError("Fehler beim Löschen der Dateien.");
    }

    setLoading(false);
  };

  const handleRename = async () => {
    if (selected.length !== 1) {
      setError("Bitte genau eine Datei auswählen.");
      return;
    }

    const file = selected[0];
    if (!newName.trim()) {
      setError("Neuer Dateiname darf nicht leer sein.");
      return;
    }

    const oldKey = `inquiries/downloads/${file.type}/${file.name}`;
    const newKey = `inquiries/downloads/${file.type}/${newName}`;

    setLoading(true);
    setError(null);

    try {
      await s3.send(
        new CopyObjectCommand({
          Bucket: S3_BUCKET,
          CopySource: `${S3_BUCKET}/${oldKey}`,
          Key: newKey,
        })
      );

      await s3.send(
        new DeleteObjectCommand({
          Bucket: S3_BUCKET,
          Key: oldKey,
        })
      );

      window.location.reload();
    } catch (err) {
      console.error(err);
      setError("Fehler beim Umbenennen.");
    }

    setLoading(false);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const isPreviewable = (name: string) => {
    return name.endsWith(".pdf") || name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg");
  };

  return (
    <div className="mt-6">
      <h3 className="font-medium mb-2">{tr('admin.manageFiles')}</h3>
      {files.length === 0 && (
        <p className="text-sm text-muted-foreground">{tr('admin.noFiles')}</p>
      )}
      {files.map((file) => (
        <div key={file.name} className="flex items-center gap-3 border rounded p-2 mb-2">
          <Checkbox
            checked={selected.some((f) => f.name === file.name)}
            onCheckedChange={(v) => toggleSelect(file, v === true)}
          />
          <span className="flex-1">{file.name}</span>
          <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>
          {isPreviewable(file.name) && (
            <Button size="sm" variant="secondary" onClick={() => previewS3File(file.type, file.name)}>
              {tr('admin.preview')}
            </Button>
          )}
          <Button size="sm" onClick={() => downloadS3File(file.type, file.name)}>
            {tr('admin.download')}
          </Button>
        </div>
      ))}
      {selected.length > 0 && (
        <Button variant="destructive" className="mt-3" onClick={handleDeleteFiles}>
          {tr('admin.delete')}
        </Button>
      )}
      {selected.length === 1 && (
        <div className="mt-4 space-y-2">
          <Input
            placeholder={tr('admin.newFilename')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button onClick={handleRename} disabled={loading}>
            {tr('admin.rename')}
          </Button>
        </div>
      )}
      {error && <p className="text-sm text-destructive mt-2">{error}</p>}
    </div>
  );
};

/* Admin */
const Admin = () => {
    const { tr } = useLanguage();
  const [passwordInput, setPasswordInput] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1"
  );
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        const s3Inquiries = await loadAllInquiriesFromS3();
        setInquiries(s3Inquiries);
      } catch (err) {
        setInquiries(getInquiries()); // fallback auf localStorage
      }
    };
    fetchInquiries();
  }, []);

  const fieldClass = "border rounded px-2 py-1 w-full";

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem(SESSION_KEY, "1");
      setLoginError("");
      return;
    }
    setLoginError("Passwort ist falsch.");
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-10 max-w-lg">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Admin Login</CardTitle>
              <p className="text-sm text-muted-foreground">
                Passwort eingeben, um Bewerbungen und Anfragen zu sehen.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-password">Passwort</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    required
                  />
                </div>
                {loginError && <p className="text-sm text-destructive">{loginError}</p>}
                <Button type="submit" className="w-full">
                  Einloggen
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold mb-6">{tr('admin.title')}</h1>
        
        <Tabs defaultValue="inquiries" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="inquiries">Anfragen</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
          </TabsList>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries" className="space-y-4 mt-6">
            {/* Upload-Formular für Admin-Dateien */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-2">{tr('admin.uploadSection')}</h2>
              <FileUploadForm />
            </div>
            <div className="space-y-4 mt-10">
              {inquiries.length === 0 && (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-muted-foreground">{tr('admin.noEntries')}</p>
                  </CardContent>
                </Card>
              )}
              {inquiries.map((inquiry) => (
                <Card key={inquiry.id}>
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="space-y-1">
                        <div>
                          <b>{tr('admin.application')} ({inquiry.type === "nurse" ? tr('admin.nurse') : inquiry.type === "employer" ? tr('admin.employer') : tr('admin.contact')})</b>
                        </div>
                        {/* Datum und Uhrzeit für alle Anfragen */}
                        <div className="mb-2">
                          <span className="font-semibold mr-2">{tr('admin.dateTime')}:</span>
                          <span>{new Date(inquiry.createdAt).toLocaleString()}</span>
                        </div>
                        <div>
                          {Object.entries(inquiry.data).map(([key, value]) => {
                            if (key === "attachmentPaths" || key === "attachmentNames") return null;
                            return (
                              <div key={key}>
                                <span className="font-semibold mr-2">{tr('admin.' + key) || key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}:</span>
                                <span>{value}</span>
                              </div>
                            );
                          })}
                        </div>
                        {inquiry.data.message && (
                          <div className="mt-2">
                            <span className="font-semibold">{tr('admin.message')}</span>
                            <div>{inquiry.data.message}</div>
                          </div>
                        )}
                        {inquiry.data.attachmentNames && (
                          <div className="mt-2">
                            <span className="font-semibold">{tr('admin.attachments')}</span>
                            <ul>
                              {inquiry.data.attachmentNames.split(", ").map((name, idx) => {
                                const paths = inquiry.data.attachmentPaths ? inquiry.data.attachmentPaths.split(", ") : [];
                                const path = paths[idx] || "";
                                return (
                                  <li key={name}>
                                    <a href={path ? `https://${S3_BUCKET}.s3.${AWS_REGION}.amazonaws.com/${path}` : undefined} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                                      {name}
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={inquiry.status === "new" ? "default" : inquiry.status === "done" ? "secondary" : "outline"}>
                          {inquiry.status === "new" ? tr('admin.statusNew') : inquiry.status === "done" ? tr('admin.statusDone') : tr('admin.statusInProgress')}
                        </Badge>
                        <Select value={inquiry.status} onValueChange={async val => {
                          const newStatus = val as InquiryStatus;
                          // Optimistisches UI-Update
                          setInquiries(prev => prev.map(i => i.id === inquiry.id ? { ...i, status: newStatus } : i));
                          // S3-Update asynchron
                          try {
                            await updateInquiryStatusOnS3(inquiry.id, newStatus);
                          } catch (err) {
                            // Fehler: Status zurücksetzen und Toast anzeigen
                            setInquiries(prev => prev.map(i => i.id === inquiry.id ? { ...i, status: inquiry.status } : i));
                            const { toast } = useToast();
                            toast({ title: tr('admin.error'), description: tr('admin.statusSaveError') });
                          }
                        }}>
                          <SelectTrigger className="min-w-[100px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">{tr('admin.statusNew')}</SelectItem>
                            <SelectItem value="in_progress">{tr('admin.statusInProgress')}</SelectItem>
                            <SelectItem value="done">{tr('admin.statusDone')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            if (inquiry.status === "done") {
                              // Lösche aus S3
                              try {
                                await s3.send(
                                  new DeleteObjectCommand({
                                    Bucket: S3_BUCKET,
                                    Key: `inquiries/data/${inquiry.id}.json`,
                                  })
                                );
                              } catch (err) {
                                alert(tr('admin.deleteS3Error'));
                                return;
                              }
                              // Lösche aus UI/local
                              setInquiries(prev => prev.filter(i => i.id !== inquiry.id));
                            } else {
                              alert(tr('admin.deleteOnlyDone'));
                            }
                          }}
                        >{tr('admin.delete')}</Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="mt-6">
            <JobManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
